import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import StreakDisplay from './StreakDisplay';
import './DailyChallengeCard.css';

const DailyChallengeCard = ({ user, onStartDaily, onSwitchToNormal, onShowPublicProfile }) => {
  const { t } = useTranslation();
  const [challenge, setChallenge] = useState(null);
  const [streak, setStreak] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch today's challenge metadata
        const challengeRes = await fetch('/api/challenges/daily');
        if (challengeRes.ok) {
          const data = await challengeRes.json();
          setChallenge(data);
        }

        // Fetch top 5 for mini-leaderboard
        const lbRes = await fetch('/api/challenges/daily/leaderboard?limit=5');
        if (lbRes.ok) {
          const lbData = await lbRes.json();
          setLeaderboard(lbData);
        }

        // Fetch streak and daily status if logged in
        if (user) {
          const [streakRes, statusRes] = await Promise.all([
            fetch('/api/challenges/streak', { credentials: 'include' }),
            fetch('/api/challenges/daily/status', { credentials: 'include' })
          ]);

          if (streakRes.ok) {
            const streakData = await streakRes.json();
            setStreak(streakData);
          }

          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.completed) {
              setAlreadyCompleted(true);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching daily challenge data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleStart = async () => {
    if (!user) {
      // Prompt login — redirect to auth
      window.location.href = '/api/auth/sso';
      return;
    }

    try {
      const res = await fetch('/api/challenges/daily/start', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.status === 409) {
        setAlreadyCompleted(true);
        return;
      }

      if (res.ok) {
        const data = await res.json();
        if (data.alreadyCompleted) {
          setAlreadyCompleted(true);
        } else if (onStartDaily) {
          onStartDaily(data);
        }
      }
    } catch (err) {
      console.error('Error starting daily challenge:', err);
    }
  };

  // Countdown to next daily challenge (midnight UTC)
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
      const diff = tomorrow - now;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="daily-challenge-card loading">
        <div className="daily-challenge-skeleton"></div>
      </div>
    );
  }

  if (!challenge) return null;

  const difficultyColors = {
    easy: '#43a047',
    medium: '#ffb300',
    hard: '#e53935'
  };

  return (
    <div className="daily-challenge-card">
      <div className="daily-challenge-header">
        <div className="daily-challenge-title-row">
          <i className="fas fa-calendar-day daily-icon"></i>
          <h3>{t('challenges.dailyTitle', 'Daily Challenge')}</h3>
        </div>
        {streak && <StreakDisplay streak={streak} />}
      </div>

      <div className="daily-challenge-body">
        <div className="daily-challenge-info">
          <span
            className="difficulty-badge"
            style={{ backgroundColor: difficultyColors[challenge.difficulty] || '#ffb300' }}
          >
            {t(`challenges.difficulty.${challenge.difficulty}`, challenge.difficulty)}
          </span>
          <span className="round-count">
            {t('challenges.rounds', { count: challenge.roundCount }, `${challenge.roundCount} rounds`)}
          </span>
        </div>

        <div className="daily-countdown">
          <i className="fas fa-clock"></i>
          <span>{t('challenges.nextIn', 'Next in')}: {timeLeft}</span>
        </div>

        {alreadyCompleted ? (
          <div className="daily-completed-section">
            <div className="daily-completed">
              <i className="fas fa-check-circle"></i>
              <span>{t('challenges.alreadyCompleted', 'Completed today!')}</span>
            </div>
            {onSwitchToNormal && (
              <button className="btn btn-daily-normal" onClick={onSwitchToNormal}>
                <i className="fas fa-gamepad"></i>
                <span>{t('game.modeNormal', 'Normal')}</span>
              </button>
            )}
          </div>
        ) : (
          <button className="btn btn-daily-play" onClick={handleStart}>
            <i className="fas fa-play"></i>
            <span>{t('challenges.playDaily', 'Play Daily')}</span>
          </button>
        )}

        {leaderboard.length > 0 && (
          <div className="daily-mini-leaderboard">
            <h4>{t('challenges.topToday', 'Top Today')}</h4>
            <ol>
              {leaderboard.map((entry, idx) => (
                <li key={idx} className="mini-lb-entry">
                  <span className="mini-lb-rank">#{entry.rank}</span>
                  <img
                    src={entry.avatarUrl || '/images/default-avatar.svg'}
                    alt=""
                    className="mini-lb-avatar"
                  />
                  <button
                    className="mini-lb-name clickable"
                    onClick={() => onShowPublicProfile && onShowPublicProfile(entry.battlenetTag)}
                    title={entry.battlenetTag}
                  >
                    {entry.displayName || entry.battlenetTag?.split('#')[0] || entry.battlenetTag}
                  </button>
                  <span className="mini-lb-score">{entry.totalScore?.toLocaleString()}</span>
                  {entry.totalTime > 0 && (
                    <span className="mini-lb-time">
                      <i className="fas fa-clock"></i> {entry.totalTime}s
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyChallengeCard;
